import { useRef, useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import useTranslation from "../hooks/useTranslation";
import { useGeneratedTextCreate } from "../hooks/useGeneratedTextCreate";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  PaperAirplaneIcon,
  CameraIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  DocumentTextIcon,
  SparklesIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { TEXT_LANGUAGES } from "../constants";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { notifySnackbar } from "../lib/notifySnackbar";
import MySelect from "./MySelect";
import MyOtherSelect from "./MyOtherSelect";
import { useUserSettings } from "../hooks/useUserSettings";
import { useStruggleWords } from "../hooks/useStruggleWords";
import { useLanguageSpace } from "../hooks/useLanguageSpace";
import {
  useTopicIdeaNext,
  type TopicIdeaDto,
} from "../hooks/useTopicIdeaNext";
import {
  usePhotoVocabularyImportCreate,
  usePhotoVocabularyImport,
  usePhotoVocabularyImportFinalize,
  usePhotoVocabularyImportPreviewTranslations,
  type PhotoVocabularyImportMode,
  type PhotoVocabularyImportWord,
} from "../hooks/usePhotoVocabularyImport";
import { invalidateQuery as invalidateVocabularyListQuery } from "../hooks/usePaginatedVocabularyList";

type FormDefaults = {
  typ?: string;
  language?: string;
  level?: string;
  tense?: string;
  word_number?: string;
  main_character?: string;
};

type FormDefaultsByLanguage = Record<string, FormDefaults>;

interface FormValues {
  typ: string;
  language: string;
  level: string;
  tense: string;
  word_number: string;
  main_character: string;
  topic: string;
  image: FileList;
}

type PhotoIntentStep = "hidden" | "intent" | "mode";
type PhotoImportState =
  | "idle"
  | "creating"
  | "processing"
  | "review"
  | "complete"
  | "error";

const INTEREST_MAP_PREFILL_STORAGE_KEY = "interest-map-prefill-topic";

export function GenerateTextCard({
  showStruggleBanner = true,
}: {
  showStruggleBanner?: boolean;
}) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const activeLanguage =
    languageSpace === "ALL"
      ? TEXT_LANGUAGES[0]?.id ?? "ENGLISH"
      : languageSpace;

  const [showTools, setShowTools] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const createGeneratedText = useGeneratedTextCreate();
  const { translate } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastHydratedDefaultsRef = useRef<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<
    "tense" | "level" | "wordNumber" | "language"
  >("tense");
  const [photoModalStep, setPhotoModalStep] =
    useState<PhotoIntentStep>("hidden");
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [photoImportState, setPhotoImportState] =
    useState<PhotoImportState>("idle");
  const [activePhotoImportId, setActivePhotoImportId] = useState<
    number | null
  >(null);
  const [photoImportMode, setPhotoImportMode] =
    useState<PhotoVocabularyImportMode | null>(null);
  const [photoImportEntries, setPhotoImportEntries] = useState<
    PhotoVocabularyImportWord[]
  >([]);
  const [selectedTokenIds, setSelectedTokenIds] = useState<string[]>([]);
  const [selectedTargetLanguages, setSelectedTargetLanguages] = useState<
    string[]
  >([]);
  const [translationResults, setTranslationResults] = useState<
    {
      token_id: string;
      vocabulary_id?: number;
      translation_language: string;
      translation_word: string;
      translation_sentence: string;
    }[]
  >([]);
  const [photoImportError, setPhotoImportError] = useState<string | null>(null);
  const selectionHydratedRef = useRef(false);

  const [formDefaultsByLanguage, setFormDefaultsByLanguage] =
    useLocalStorage<FormDefaultsByLanguage>("per-language-create-settings-v1", {});
  const currentDefaults: FormDefaults = useMemo(
    () => ({
      level: "unselected",
      typ: "unselected",
      tense: "unselected",
      word_number: "unselected",
      main_character: "",
      language: activeLanguage,
      ...formDefaultsByLanguage[activeLanguage],
    }),
    [activeLanguage, formDefaultsByLanguage],
  );
  const queryClient = useQueryClient();
  const createPhotoImport = usePhotoVocabularyImportCreate();
  const finalizePhotoImport = usePhotoVocabularyImportFinalize();
  const previewTranslations = usePhotoVocabularyImportPreviewTranslations();
  const photoImportQuery = usePhotoVocabularyImport(activePhotoImportId);
  const userSettings = useUserSettings({ enabled: true });
  const struggleWords = useStruggleWords(activeLanguage);
  const struggleWordsForLanguage = useMemo(
    () =>
      (struggleWords.data || []).filter(
        (entry) => entry.word_language === activeLanguage,
      ),
    [struggleWords.data, activeLanguage],
  );
  const [isGeneratingStruggleText, setIsGeneratingStruggleText] =
    useState(false);
  const [readyTopicIdea, setReadyTopicIdea] = useState<TopicIdeaDto | null>(null);
  const [appliedTopicIdea, setAppliedTopicIdea] = useState<TopicIdeaDto | null>(
    null,
  );
  const topicIdeaNext = useTopicIdeaNext();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();
  const [topicValue] = watch(["topic"]);
  const [formLanguage] = watch(["language"]);
  const [formTyp, formLevel, formTense, formWordNumber, formMainCharacter] =
    watch(["typ", "level", "tense", "word_number", "main_character"]);
  const isKnowledgeSelected = formTyp === "KNOWLEDGE";

  useEffect(() => {
    // Hydrate form controls when switching language spaces with per-language defaults.
    const nextLanguage = currentDefaults.language ?? activeLanguage;
    const nextLevel = currentDefaults.level ?? "unselected";
    const nextTyp = currentDefaults.typ ?? "unselected";
    const nextTense = currentDefaults.tense ?? "unselected";
    const nextWordNumber = currentDefaults.word_number ?? "unselected";
    const nextMainCharacter = currentDefaults.main_character ?? "";

    const nextSnapshot = JSON.stringify([
      nextLanguage,
      nextLevel,
      nextTyp,
      nextTense,
      nextWordNumber,
      nextMainCharacter,
    ]);
    if (lastHydratedDefaultsRef.current === nextSnapshot) {
      return;
    }
    lastHydratedDefaultsRef.current = nextSnapshot;

    setValue("language", nextLanguage);
    setValue("level", nextLevel);
    setValue("typ", nextTyp);
    setValue("tense", nextTense);
    setValue("word_number", nextWordNumber);
    setValue("main_character", nextMainCharacter);
  }, [
    activeLanguage,
    currentDefaults.language,
    currentDefaults.level,
    currentDefaults.typ,
    currentDefaults.tense,
    currentDefaults.word_number,
    currentDefaults.main_character,
    setValue,
  ]);

  useEffect(() => {
    const prefilledTopic = sessionStorage.getItem(INTEREST_MAP_PREFILL_STORAGE_KEY);
    if (!prefilledTopic?.trim()) {
      return;
    }
    applyTopicSuggestion(prefilledTopic);
    sessionStorage.removeItem(INTEREST_MAP_PREFILL_STORAGE_KEY);
  }, []);

  const topicRegister = register("topic", {
    required: false,
  });
  const photoImportLoading =
    photoImportState === "creating" || photoImportState === "processing";
  const finalizeDisabled =
    selectedTokenIds.length === 0 ||
    translationResults.length === 0 ||
    finalizePhotoImport.isPending;
  useEffect(() => {
    const data = photoImportQuery.data;
    if (!data) {
      return;
    }
    if (data.status === "COMPLETE") {
      if (!selectionHydratedRef.current) {
        const normalizedEntries = (data.result_payload ?? []).map(
          (entry, index) => ({
            ...entry,
            token_id: entry.token_id || `${Date.now()}-${index}`,
          }),
        );
        setPhotoImportEntries(normalizedEntries);
        setSelectedTokenIds(
          normalizedEntries.map((entry) => entry.token_id),
        );
        selectionHydratedRef.current = true;
        setPhotoImportError(null);
        if (
          photoImportState !== "review" &&
          photoImportState !== "complete"
        ) {
          setPhotoImportState("review");
        }
        if (selectedTargetLanguages.length === 0) {
          const entryLangs = normalizedEntries
            .map((e) => (e.language || "").trim())
            .filter(Boolean);
          const uniqueEntryLangs = Array.from(new Set(entryLangs));
          const defaultLang =
            formLanguage && formLanguage !== "unselected"
              ? formLanguage
              : undefined;
          if (
            userSettings.data?.mother_tongue &&
            uniqueEntryLangs.length > 0 &&
            uniqueEntryLangs.every(
              (lang) => lang !== userSettings.data?.mother_tongue,
            )
          ) {
            setSelectedTargetLanguages([userSettings.data.mother_tongue]);
          } else if (defaultLang) {
            setSelectedTargetLanguages([defaultLang]);
          }
        }
      }
    } else if (data.status === "FAILED" && photoImportState !== "error") {
      setPhotoImportState("error");
      setPhotoImportEntries([]);
      setSelectedTokenIds([]);
      setPhotoImportError(
        data.error_message || translate("photo_import_no_words"),
      );
      selectionHydratedRef.current = false;
    }
  }, [
    photoImportQuery.data,
    photoImportState,
    translate,
    selectedTargetLanguages.length,
    formLanguage,
    userSettings.data?.mother_tongue,
  ]);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      return;
    }
    if (photoImportState !== "idle") {
      notifySnackbar({
        message: translate("photo_import_in_progress"),
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    setValue("image", event.target.files as FileList);
    setPendingImageFile(file);
    setPhotoModalStep("intent");
    setPhotoImportEntries([]);
    setSelectedTokenIds([]);
    setSelectedTargetLanguages([]);
    setTranslationResults([]);
    setActivePhotoImportId(null);
    setPhotoImportMode(null);
    setPhotoImportError(null);
    selectionHydratedRef.current = false;
  };

  const handleImageDelete = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    setPendingImageFile(null);
    setPhotoModalStep("hidden");
    // Clear form value
    setValue("image", new DataTransfer().files as FileList);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePhotoIntentChoice = (choice: "text" | "import") => {
    if (choice === "text") {
      setPhotoModalStep("hidden");
      setPendingImageFile(null);
      return;
    }
    setPhotoModalStep("mode");
  };

  const resetPhotoImportFlow = () => {
    setPhotoImportState("idle");
    setActivePhotoImportId(null);
    setPhotoImportEntries([]);
    setSelectedTokenIds([]);
    setSelectedTargetLanguages([]);
    setTranslationResults([]);
    setPhotoImportMode(null);
    setPhotoImportError(null);
    setPhotoModalStep("hidden");
    selectionHydratedRef.current = false;
  };

  const handleGenerateStruggleText = async () => {
        const struggleList = struggleWordsForLanguage;
        if (!struggleList.length) {
          notifySnackbar({ message: translate("struggle_words_empty") });
          return;
        }
    if (
      [formTyp, formLanguage, formLevel, formTense, formWordNumber].some(
        (value) => !value || value === "unselected",
      )
    ) {
      notifySnackbar({
        message: translate("struggle_words_require_form"),
      });
      return;
    }
    setIsGeneratingStruggleText(true);
    try {
      // Use a short, generic title to avoid backend validation issues.
      const topic = translate("struggle_words_topic_generic");

      const created = await createGeneratedText.mutateAsync({
        createReepriseDto: {
          text_type: formTyp,
          language: formLanguage,
          level: formLevel,
          tense: formTense,
          word_number: Number(formWordNumber),
          topic,
          main_character: isKnowledgeSelected ? "" : formMainCharacter ?? "",
        },
      });
      // Persist current selections for next time
      setFormDefaultsByLanguage((prev) => ({
        ...prev,
        [activeLanguage]: {
          main_character: isKnowledgeSelected ? "" : formMainCharacter ?? "",
          level: formLevel,
          tense: formTense,
          language: activeLanguage,
          typ: formTyp,
          word_number: formWordNumber ?? "unselected",
        },
      }));
      if (created?.id) {
        navigate({
          to: "/app/reeprise/$generatedTextId",
          params: { generatedTextId: `${created.id}` },
          search: {
            chapter: undefined,
            focusWord: undefined,
            focusSentence: undefined,
            focusToken: undefined,
          },
        });
      }
    } catch (error) {
      console.error(error);
      notifySnackbar({ message: translate("struggle_words_failed") });
    } finally {
      setIsGeneratingStruggleText(false);
    }
  };

  const startPhotoImport = async (mode: PhotoVocabularyImportMode) => {
    if (!pendingImageFile) {
      notifySnackbar({ message: translate("photo_import_no_words") });
      return;
    }
    setPhotoImportState("creating");
    setPhotoImportError(null);
    selectionHydratedRef.current = false;
    try {
      const record = await createPhotoImport.mutateAsync({
        file: pendingImageFile,
        mode,
      });
      setPhotoImportMode(mode);
      setActivePhotoImportId(record.id);
      setPhotoImportState("processing");
      setPhotoModalStep("hidden");
      setPendingImageFile(null);
      handleImageDelete();
      const defaultLang =
        formLanguage && formLanguage !== "unselected"
          ? formLanguage
          : undefined;
      setSelectedTargetLanguages(defaultLang ? [defaultLang] : []);
    } catch (error) {
      console.error(error);
      setPhotoImportState("error");
      setPhotoImportError(
        error instanceof Error
          ? error.message
          : translate("photo_import_error_title"),
      );
    }
  };

  const toggleTokenSelection = (tokenId: string) => {
    setSelectedTokenIds((prev) =>
      prev.includes(tokenId)
        ? prev.filter((id) => id !== tokenId)
        : [...prev, tokenId],
    );
    setTranslationResults([]);
  };

  const selectAllTokens = () => {
    setSelectedTokenIds(
      photoImportEntries
        .map((entry) => entry.token_id)
        .filter((tokenId): tokenId is string => Boolean(tokenId)),
    );
    setTranslationResults([]);
  };

  const clearTokenSelection = () => {
    setSelectedTokenIds([]);
    setTranslationResults([]);
  };

  const toggleTargetLanguage = (lang: string) => {
    setSelectedTargetLanguages((prev) =>
      prev.includes(lang) ? prev.filter((id) => id !== lang) : [...prev, lang],
    );
    setTranslationResults([]);
  };

  const handleFinalizeImport = async () => {
    if (
      !activePhotoImportId ||
      selectedTokenIds.length === 0
    ) {
      return;
    }
    try {
      const result = await finalizePhotoImport.mutateAsync({
        importId: activePhotoImportId,
        tokenIds: selectedTokenIds,
        targetLanguages: selectedTargetLanguages,
        learningLanguage:
          formLanguage && formLanguage !== "unselected"
            ? formLanguage
            : undefined,
      });
      notifySnackbar({
        message: translate("photo_import_finalize_success"),
      });
      invalidateVocabularyListQuery(queryClient);
      setTranslationResults(result.translations ?? []);
      setPhotoImportState("complete");
      selectionHydratedRef.current = false;
    } catch (error) {
      console.error(error);
      setPhotoImportState("error");
      setPhotoImportError(
        error instanceof Error
          ? error.message
          : translate("photo_import_error_title"),
      );
    }
  };

  const handlePreviewTranslations = async () => {
    if (!activePhotoImportId || selectedTokenIds.length === 0) {
      return;
    }
    try {
      const result = await previewTranslations.mutateAsync({
        importId: activePhotoImportId,
        tokenIds: selectedTokenIds,
        targetLanguages: selectedTargetLanguages,
        learningLanguage:
          formLanguage && formLanguage !== "unselected"
            ? formLanguage
            : undefined,
      });
      setTranslationResults(result.translations ?? []);
    } catch (error) {
      console.error(error);
      notifySnackbar({
        message:
          error instanceof Error
            ? error.message
            : translate("photo_import_error_title"),
      });
    }
  };

  const photoImportSelectionSet = useMemo(
    () => new Set(selectedTokenIds),
    [selectedTokenIds],
  );

  const applyTopicSuggestion = (topic: string) => {
    setValue("topic", topic, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    inputRef.current?.focus();
  };

  const fetchTopicIdea = async () => {
    return topicIdeaNext.mutateAsync({
      language:
        formLanguage && formLanguage !== "unselected"
          ? formLanguage
          : activeLanguage,
      text_type: formTyp && formTyp !== "unselected" ? formTyp : undefined,
    });
  };

  const handleRandomTopicSuggestion = () => {
    const useIdea = readyTopicIdea;
    const applyIdea = (idea: TopicIdeaDto) => {
      applyTopicSuggestion(idea.topic);
      setAppliedTopicIdea(idea);
      setReadyTopicIdea(null);
    };

    if (useIdea) {
      applyIdea(useIdea);
      return;
    }

    void fetchTopicIdea()
      .then((idea) => {
        applyIdea(idea);
      })
      .catch(() => {
        notifySnackbar({
          message: translate("topic_surprise_me_failed"),
        });
      });
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    console.log(JSON.stringify(data));
    const mainCharacterToUse =
      data.typ === "KNOWLEDGE" ? "" : data.main_character;
    const normalizedInputTopic = data.topic.trim();
    const usedSuggestedTopic =
      appliedTopicIdea && normalizedInputTopic === appliedTopicIdea.topic.trim()
        ? appliedTopicIdea
        : null;
    void createGeneratedText
      .mutateAsync({
        createReepriseDto: {
          text_type: data.typ,
          language: data.language,
          level: data.level,
          tense: data.tense,
          word_number: Number(data.word_number),
          topic: data.topic,
          main_character: mainCharacterToUse,
          topic_source: usedSuggestedTopic ? "IDEA" : "MANUAL",
          topic_suggestion_id: usedSuggestedTopic?.id,
          image: data.image,
        },
      })
      .then((created) => {
        setFormDefaultsByLanguage((prev) => {
          const prevEntry = prev[activeLanguage];
          const prevMain = prevEntry?.main_character ?? "";
          // Knowledge submissions should not overwrite the last saved main character.
          const nextMainCharacter =
            data.typ === "KNOWLEDGE" ? prevMain : mainCharacterToUse;
          return {
            ...prev,
            [activeLanguage]: {
              main_character: nextMainCharacter,
              level: data.level,
              tense: data.tense,
              language: activeLanguage,
              typ: data.typ,
              word_number: data.word_number,
            },
          };
        });
        // Clear topic field and image after successful generation
        setValue("topic", "");
        setAppliedTopicIdea(null);
        setReadyTopicIdea(null);
        handleImageDelete();
        if (created?.id) {
          navigate({
            to: "/app/reeprise/$generatedTextId",
            params: { generatedTextId: `${created.id}` },
            search: {
              chapter: undefined,
              focusWord: undefined,
              focusSentence: undefined,
              focusToken: undefined,
            },
          });
        }
      });
    setShowTools(false);
  };

  const onError = () => {
    // Check if there are validation errors in the required fields
    const hasRequiredFieldErrors =
      errors.language ||
      errors.level ||
      errors.tense ||
      errors.typ ||
      errors.word_number;

    // If there are errors and the details are collapsed, expand them so user can see the errors
    if (hasRequiredFieldErrors && showTools) {
      setShowTools(false);
    }
  };

  const [levelFormFieldValue] = watch(["level"]);
  // On first render level can be undefined; fall back to per-language defaults.
const selectedLevel = levelFormFieldValue ?? currentDefaults.level;
  let languageOptions = [] as string[];
  if (selectedLevel === "unselected") {
    languageOptions = [];
  } else if (selectedLevel === "A0") {
    languageOptions = ["A"];
  } else if (selectedLevel === "A1") {
    languageOptions = ["A", "C", "E"];
  } else if (selectedLevel === "A2") {
    languageOptions = ["A", "B", "C", "E"];
  } else if (selectedLevel === "B1") {
    languageOptions = ["A", "B", "C", "D"];
  } else if (selectedLevel === "B2") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else if (selectedLevel === "C1") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else if (selectedLevel === "C2") {
    languageOptions = ["A", "B", "C", "D", "E", "F"];
  } else {
    console.error("NOT IMPLEMENTED YET", selectedLevel);
  }

  // Cleanup image preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "0px";
    const nextHeight = Math.max(textarea.scrollHeight, 44);
    textarea.style.height = `${nextHeight}px`;
  }, [topicValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="px-0 w-full">
      <div className="flex flex-col w-full rounded-2xl shadow-md border border-gray-200 bg-neutral-50 px-4 pt-4 pb-4">
        <div className="flex items-start gap-2">
          <textarea
            {...topicRegister}
            ref={(el) => {
              topicRegister.ref(el);
              inputRef.current = el;
            }}
            rows={1}
            placeholder={translate("form_topic_placeholder")}
            className="min-h-[44px] flex-1 resize-none overflow-hidden border-none bg-transparent text-base leading-6 placeholder-gray-400 focus:outline-none"
          />
          {imagePreviewUrl && (
            <div className="flex items-center pr-2">
              <img
                src={imagePreviewUrl}
                alt="preview"
                className="w-8 h-8 object-cover rounded-sm border"
              />
              <button
                type="button"
                onClick={handleImageDelete}
                className="ml-1 text-gray-500 hover:text-red-500"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />

        <div className="flex justify-between items-center mt-2 text-gray-500">
          <div className="flex items-center gap-4 flex-1">
            <CameraIcon
              className="h-6 w-6 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            />
            <button type="button" onClick={() => setShowTools(!showTools)}>
              <AdjustmentsHorizontalIcon className="h-6 w-6" />
            </button>
          </div>
            <MyOtherSelect
            options={[
              ["STORY", translate("text_type_story")],
              ["DIALOGUE", translate("text_type_dialogue")],
              ["CRIME", translate("text_type_crime")],
              ["PODCAST", translate("text_type_podcast")],
              ["KNOWLEDGE", translate("text_type_knowledge")],
            ]}
            defaultValue={currentDefaults.typ ?? "unselected"}
            {...register("typ", {
              required: true,
              validate: (value) => value !== "unselected",
            })}
          />
          <div className="flex items-center gap-4 ml-2">
            <button
              type="button"
              onClick={handleRandomTopicSuggestion}
              disabled={topicIdeaNext.isPending}
              className="flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
              aria-label={translate("topic_surprise_me")}
              title={translate("topic_surprise_me")}
            >
              <SparklesIcon className="h-4 w-4" />
              <span>
                {topicIdeaNext.isPending
                  ? translate("topic_surprise_me_loading")
                  : translate("topic_surprise_me_short")}
              </span>
            </button>
            <button
              type="submit"
              className={`rounded-full p-1 ${topicValue?.trim() || imagePreviewUrl ? "bg-green-200" : "bg-transparent"}`}
            >
              <PaperAirplaneIcon
                className={`h-6 w-6 rotate-45 ${topicValue?.trim() || imagePreviewUrl ? "text-gray-700" : "text-gray-300"}`}
              />
            </button>
          </div>
        </div>
        {showStruggleBanner &&
          struggleWords.isSuccess &&
          struggleWordsForLanguage.length > 0 && (
          <div className="relative mt-3 overflow-hidden rounded-xl border border-sky-200 bg-[linear-gradient(180deg,#9bd8ff_0%,#c9ebff_52%,#edf8ff_100%)] p-3">
            <div className="relative">
              <p className="text-sm font-semibold text-sky-900">
                {translate("struggle_words_title")}
              </p>
              <p className="mb-2 text-xs text-sky-900/85">
                {translate("struggle_words_description", {
                  count: `${struggleWordsForLanguage.length}`,
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={handleGenerateStruggleText}
              disabled={isGeneratingStruggleText}
              className="w-full rounded-full border border-sky-400 bg-sky-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.1em] text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:border-sky-200 disabled:bg-sky-300"
            >
              {isGeneratingStruggleText
                ? translate("struggle_words_generating")
                : translate("struggle_words_generate")}
            </button>
          </div>
        )}
        <div
          className={`${showTools ? "mt-4 p-4 border border-gray-200 rounded-xl bg-gray-50" : "hidden"}`}
        >
          <div className="mb-4">
          <label className="text-left block text-sm font-medium text-gray-700 mb-1">
            {translate("main_character")}
          </label>
          <input
            type="text"
            {...register("main_character")}
            defaultValue={currentDefaults.main_character ?? ""}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none text-sm ${
              isKnowledgeSelected ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
            disabled={isKnowledgeSelected}
            placeholder={translate("main_character_placeholder")}
          />
            <div className="mt-2 flex flex-wrap gap-2">
              {["Ich", "Anna", "Enrico"].map((name) => (
                <button
                  key={name}
                  onClick={() => {
                    if (isKnowledgeSelected) return;
                    setValue("main_character", name);
                  }}
                  className={`text-sm px-3 py-1 border rounded-full bg-gray-100 hover:bg-gray-200 ${
                    isKnowledgeSelected ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  type="button"
                  disabled={isKnowledgeSelected}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex gap-4 text-sm font-medium text-gray-700">
              {[
                { key: "level", label: translate("form_level") },
                { key: "wordNumber", label: translate("form_wordcount_label") },
                { key: "tense", label: translate("form_tense") },
                { key: "language", label: translate("form_language") },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() =>
                    setSelectedTab(
                      tab.key as "tense" | "level" | "wordNumber" | "language",
                    )
                  }
                  className={`pb-1 border-b-2 min-w-fit transition-all duration-200 ${
                    selectedTab === tab.key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className={selectedTab === "language" ? "" : "hidden"}>
              <MySelect
                options={[
                  [
                    activeLanguage,
                    translate(
                      TEXT_LANGUAGES.find((t) => t.id === activeLanguage)
                        ?.prettyTranslationKey ?? "language_space_current",
                    ),
                  ],
                ]}
                defaultValue={currentDefaults.language ?? activeLanguage}
                {...register("language", {
                  required: true,
                  validate: () => true,
                })}
              />
            </div>
            <div className={selectedTab === "level" ? "" : "hidden"}>
              <MySelect
                defaultValue={currentDefaults.level ?? "unselected"}
                options={[
                  ["A0", "A0"],
                  ["A1", "A1"],
                  ["A2", "A2"],
                  ["B1", "B1"],
                  ["B2", "B2"],
                  ["C1", "C1"],
                  ["C2", "C2"],
                ]}
                {...register("level", {
                  required: true,
                  validate: (value) => value !== "unselected",
                })}
              />
            </div>
            <div className={selectedTab === "tense" ? "" : "hidden"}>
              <MySelect
                options={[
                  ["A", translate("form_tense_present")],
                  ["B", translate("form_tense_past")],
                  ["C", translate("form_tense_present_perfect")],
                  ["D", translate("form_tense_past_perfect")],
                  ["E", translate("form_tense_future_I")],
                  ["F", translate("form_tense_future_perfect")],
                ].filter((v): v is [string, string] =>
                  languageOptions.includes(v[0]),
                )}
                defaultValue={currentDefaults.tense ?? "unselected"}
                {...register("tense", {
                  required: true,
                  validate: (value) => value !== "unselected",
                })}
              />
            </div>
            <div className={selectedTab === "wordNumber" ? "" : "hidden"}>
              <MySelect
                options={[
                  ["50", "50"],
                  ["100", "100"],
                  ["200", "200"],
                  ["300", "300"],
                ]}
                defaultValue={currentDefaults.word_number ?? "unselected"}
                {...register("word_number", {
                  required: true,
                  validate: (value) => value !== "unselected",
                })}
              />
            </div>
          </div>
        </div>
      </div>
      {photoImportState !== "idle" && (
        <PhotoImportPanel
        translate={translate}
        state={photoImportState}
        entries={photoImportEntries}
        selectionSet={photoImportSelectionSet}
        onToggleToken={toggleTokenSelection}
          onSelectAll={selectAllTokens}
          onClearSelection={clearTokenSelection}
          onFinalize={handleFinalizeImport}
          onDismiss={resetPhotoImportFlow}
          finalizeDisabled={finalizeDisabled}
          isFinalizing={finalizePhotoImport.isPending}
        mode={photoImportMode}
        loading={photoImportLoading}
        errorMessage={photoImportError}
        targetLanguages={selectedTargetLanguages}
        onToggleTargetLanguage={toggleTargetLanguage}
        availableLanguages={TEXT_LANGUAGES}
        translationResults={translationResults}
        onPreviewTranslations={handlePreviewTranslations}
        previewLoading={previewTranslations.isPending}
      />
      )}
      {photoModalStep !== "hidden" && pendingImageFile && (
        <PhotoIntentModal
          step={photoModalStep}
          onSelectIntent={handlePhotoIntentChoice}
          onSelectMode={startPhotoImport}
          onClose={() => {
            setPhotoModalStep("hidden");
            setPendingImageFile(null);
          }}
          translate={translate}
          onBack={() => setPhotoModalStep("intent")}
        />
      )}
    </form>
  );
}

function PhotoIntentModal(props: {
  step: PhotoIntentStep;
  onSelectIntent: (choice: "text" | "import") => void;
  onSelectMode: (mode: PhotoVocabularyImportMode) => void;
  onClose: () => void;
  translate: (key: string) => string;
  onBack?: () => void;
}) {
  const { step, onSelectIntent, onSelectMode, onClose, translate, onBack } =
    props;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {step === "intent"
              ? translate("photo_import_prompt_title")
              : translate("photo_import_mode_title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {step === "intent" ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => onSelectIntent("text")}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-slate-100 p-3 text-slate-600">
                <DocumentTextIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_prompt_generate_text")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_generate_text_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectIntent("import")}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-slate-100 p-3 text-slate-600">
                <SparklesIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_prompt_import_vocab")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_vocab_desc")}
                </p>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => onSelectMode("ALL_WORDS")}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-blue-50 p-3 text-blue-600">
                <PhotoIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_mode_all_words")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_vocab_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectMode("HIGHLIGHTED_ONLY")}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-4 text-left transition hover:border-slate-400"
            >
              <div className="rounded-full bg-amber-50 p-3 text-amber-600">
                <SparklesIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {translate("photo_import_mode_highlighted_words")}
                </p>
                <p className="text-xs text-slate-500">
                  {translate("photo_import_prompt_import_vocab_desc")}
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                onBack?.();
              }}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700"
            >
              {translate("photo_import_mode_back")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoImportPanel(props: {
  translate: (key: string) => string;
  state: PhotoImportState;
  entries: PhotoVocabularyImportWord[];
  selectionSet: Set<string>;
  onToggleToken: (tokenId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onFinalize: () => void;
  onDismiss: () => void;
  finalizeDisabled: boolean;
  isFinalizing: boolean;
  mode: PhotoVocabularyImportMode | null;
  loading: boolean;
  errorMessage: string | null;
  targetLanguages: string[];
  onToggleTargetLanguage: (lang: string) => void;
  availableLanguages: typeof TEXT_LANGUAGES;
  translationResults: {
    token_id: string;
    vocabulary_id?: number;
    translation_language: string;
    translation_word: string;
    translation_sentence: string;
  }[];
  onPreviewTranslations: () => void;
  previewLoading: boolean;
}) {
  const {
    translate,
    state,
    entries,
    selectionSet,
    onToggleToken,
    onSelectAll,
    onClearSelection,
    onFinalize,
    onDismiss,
    finalizeDisabled,
    isFinalizing,
    mode,
    loading,
    errorMessage,
    targetLanguages,
    onToggleTargetLanguage,
    availableLanguages,
    translationResults,
    onPreviewTranslations,
    previewLoading,
  } = props;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-slate-500" />
          <p className="text-base font-semibold text-slate-900">
            {translate("photo_import_processing_title")}
          </p>
          <p className="text-sm text-slate-500">
            {translate("photo_import_processing_subtitle")}
          </p>
        </div>
      );
    }
    if (state === "error") {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-rose-500" />
          <p className="text-base font-semibold text-slate-900">
            {translate("photo_import_error_title")}
          </p>
          <p className="text-sm text-slate-500">
            {errorMessage || translate("photo_import_no_words")}
          </p>
          <button
            type="button"
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
            onClick={onDismiss}
          >
            {translate("photo_import_error_retry")}
          </button>
        </div>
      );
    }
    if (state === "complete") {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircleIcon className="h-10 w-10 text-emerald-500" />
          <p className="text-base font-semibold text-slate-900">
            {translate("photo_import_section_complete")}
          </p>
          <Link
            to="/app/vocabulary_progress"
            search={(prev) => ({ ...prev, tab: "photo" })}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
            onClick={onDismiss}
          >
            {translate("vocabulary_tab_photo")}
          </Link>
          <button
            type="button"
            className="text-sm font-semibold text-slate-500"
            onClick={onDismiss}
          >
            {translate("photo_import_dismiss")}
          </button>
        </div>
      );
    }
    if (state === "review") {
      return (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {translate("photo_import_review_title")}
            </p>
            <p className="text-xs text-slate-500">
              {translate("photo_import_review_description")}
            </p>
          </div>
          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {translate("photo_import_translation_targets") ??
                "Translation targets"}
            </p>
            <div className="flex flex-wrap gap-2">
              {availableLanguages.map((lang) => {
                const selected = targetLanguages.includes(lang.id);
                return (
                  <button
                    key={lang.id}
                    type="button"
                    onClick={() => onToggleTargetLanguage(lang.id)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      selected
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {translate(lang.prettyTranslationKey)}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500">
              {translate("photo_import_translation_hint") ??
                "Choose the languages you want translations in."}
            </p>
            <button
              type="button"
              onClick={onPreviewTranslations}
              disabled={previewLoading}
              className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {previewLoading
                ? translate("photo_import_processing_subtitle")
                : translate("photo_import_generate_translations") ??
                  "Generate translations"}
            </button>
          </div>
          {entries.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
              {translate("photo_import_no_words")}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600">
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="rounded-full border border-slate-200 px-3 py-1 hover:border-slate-400"
                >
                  {translate("photo_import_select_all")}
                </button>
                <button
                  type="button"
                  onClick={onClearSelection}
                  className="rounded-full border border-slate-200 px-3 py-1 hover:border-slate-400"
                >
                  {translate("photo_import_clear_all")}
                </button>
                <span className="text-slate-400">•</span>
                <span>
                  {selectionSet.size} / {entries.length}
                </span>
              </div>
              <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200">
                {entries.map((entry) => (
                  <label
                    key={entry.token_id}
                    className="flex cursor-pointer gap-3 p-3"
                  >
                    <input
                      type="checkbox"
                      checked={selectionSet.has(entry.token_id)}
                      onChange={() => onToggleToken(entry.token_id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                    />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-slate-900">
                      {entry.word}
                    </p>
                    {translationResults
                      .filter((tr) => tr.token_id === entry.token_id)
                      .map((tr) => {
                        const lang =
                          availableLanguages.find(
                            (l) => l.id === tr.translation_language,
                          )?.short ?? tr.translation_language;
                        return (
                          <p
                            key={`${tr.token_id}-${tr.translation_language}-word`}
                            className="text-xs text-slate-600"
                          >
                            <span className="font-semibold text-slate-500">
                              {lang}:
                            </span>{" "}
                            {tr.translation_word}
                          </p>
                        );
                      })}
                    {entry.sentence && (
                      <p className="text-xs text-slate-500">
                        <span className="font-semibold text-slate-600">
                          {translate("photo_import_sentence_label")}:
                        </span>{" "}
                        {entry.sentence}
                      </p>
                    )}
                    {translationResults
                      .filter((tr) => tr.token_id === entry.token_id)
                      .map((tr) => {
                        const lang =
                          availableLanguages.find(
                            (l) => l.id === tr.translation_language,
                          )?.short ?? tr.translation_language;
                        if (!tr.translation_sentence) {
                          return null;
                        }
                        return (
                          <p
                            key={`${tr.token_id}-${tr.translation_language}-sentence`}
                            className="text-[11px] text-slate-500"
                          >
                            <span className="font-semibold text-slate-500">
                              {lang} — {translate("photo_import_sentence_label")}:
                            </span>{" "}
                            {tr.translation_sentence}
                          </p>
                        );
                      })}
                    <div className="flex flex-wrap gap-3 text-[11px] uppercase tracking-wide text-slate-400">
                      {entry.language && (
                        <span>
                          {translate("photo_import_language_label")}:{" "}
                          <span className="text-slate-600">
                              {entry.language}
                            </span>
                          </span>
                        )}
                        {typeof entry.confidence === "number" && (
                          <span>
                            {translate("photo_import_confidence_label")}:{" "}
                            <span className="text-slate-600">
                              {Math.round(entry.confidence * 100)}%
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400"
                  onClick={onClearSelection}
                >
                  {translate("photo_import_deselect_all")}
                </button>
              </div>
              <button
                type="button"
                className="w-full rounded-full bg-slate-900 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={onFinalize}
                disabled={finalizeDisabled}
              >
                {isFinalizing
                  ? translate("photo_import_processing_subtitle")
                  : translate("photo_import_finalize_button")}
              </button>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-lg flex max-h-[75vh] flex-col">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {translate("photo_import_section_title")}
          </p>
          {mode && (
            <p className="text-xs text-slate-500">
              {mode === "ALL_WORDS"
                ? translate("photo_import_mode_all_words")
                : translate("photo_import_mode_highlighted_words")}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={loading}
          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={onDismiss}
        >
          {translate("photo_import_dismiss")}
        </button>
      </div>
      <div className="mt-4 flex-1 overflow-y-auto pr-1">{renderContent()}</div>
    </div>
  );
}
